'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, Heart, Microscope, Activity, Brain, Target } from "lucide-react";
import { useParams } from "next/navigation";
// import { localizeSpelling } from "@/lib/spelling-localization";

// Temporary fallback function
const localizeSpelling = (text: string, locale: string) => text;

const medicalImagingData = {
  title: "Medical Imaging Analysis",
  subtitle: "Deep learning-based anomaly detection for X-rays, MRIs, and CT scans with automated prioritization of urgent cases.",
  headerGradient: "bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600",
  accentColor: "text-cyan-600",
  buttonColor: "bg-cyan-600 hover:bg-cyan-700",
  challenge: "Healthcare providers require faster, more accurate diagnostic capabilities for medical imagery while reducing radiologist workload and improving early detection rates.",
  solutionHighlights: [
    "Deep learning-based anomaly detection for X-rays, MRIs, and CT scans",
    "Automated prioritization of urgent cases in radiology workflows",
    "Comparative analysis with historical patient data",
    "3D reconstruction and visualization for surgical planning"
  ],
  benefits: [
    "92% accuracy in anomaly detection",
    "60% reduction in analysis time",
    "45% improvement in early diagnosis rates",
    "Enhanced workflow efficiency for radiologists"
  ],
  techStack: ["Computer Vision", "Deep Learning", "Medical Imaging Analysis", "Cloud Computing", "Healthcare AI", "DICOM Integration"],
  imagingStats: {
    detectionAccuracy: "92%",
    analysisTime: "28s",
    falsePositiveRate: "3.2%",
    casesProcessed: "2.4M+"
  }
};

// Safe localization helper that falls back to original text if localization fails
const safeLocalizeSpelling = (text: string, locale: string) => {
  try {
    return localizeSpelling(text, locale);
  } catch (error) {
    console.warn(`Localization failed for "${text}" in locale "${locale}":`, error);
    return text;
  }
};

export default function MedicalImagingAnalysisPage() {
  // Since this is a client component, we use useParams hook
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  // Localize all text content based on current locale
  const localizedData = {
    ...medicalImagingData,
    title: safeLocalizeSpelling(medicalImagingData.title, currentLocale),
    subtitle: safeLocalizeSpelling(medicalImagingData.subtitle, currentLocale),
    challenge: safeLocalizeSpelling(medicalImagingData.challenge, currentLocale),
    solutionHighlights: medicalImagingData.solutionHighlights.map(
      highlight => safeLocalizeSpelling(highlight, currentLocale)
    ),
    benefits: medicalImagingData.benefits.map(
      benefit => safeLocalizeSpelling(benefit, currentLocale)
    ),
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${localizedData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-cyan-100 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              {safeLocalizeSpelling("Back to Portfolio", currentLocale)}
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {localizedData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-cyan-100 max-w-3xl mx-auto">
            {localizedData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {localizedData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* Medical Imaging Analysis Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${localizedData.accentColor}`}>
              {safeLocalizeSpelling("AI Diagnostic System", currentLocale)}
            </h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>{safeLocalizeSpelling("Medical Image Analysis", currentLocale)}</span>
                <span className="text-xs bg-cyan-500/90 px-2 py-0.5 rounded-full flex items-center">
                  <Microscope size={12} className="mr-1"/>
                  {safeLocalizeSpelling("Analyzing", currentLocale)}
                </span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Medical Imaging Frame */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-full h-full bg-slate-800 rounded-lg relative overflow-hidden shadow-inner border border-slate-700 flex flex-col p-4">
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <Brain size={18} className="text-cyan-400 mr-1" />
                        <span className="text-slate-200 font-medium">{safeLocalizeSpelling("CT Scan Analysis", currentLocale)}</span>
                      </div>
                      <div className="bg-cyan-900/50 text-cyan-200 px-2 py-0.5 rounded text-xs font-medium">
                        {safeLocalizeSpelling("URGENT", currentLocale)}
                      </div>
                    </div>
                    
                    <div className="mt-10 grid grid-cols-2 gap-4 h-full">
                      {/* Medical scan visualization */}
                      <div className="flex items-center justify-center relative h-full">
                        {/* Stylized CT scan representation */}
                        <div className="w-full h-4/5 bg-black rounded relative">
                          {/* Simulated CT scan with an anomaly highlight */}
                          <div className="absolute inset-0 bg-gradient-to-b from-slate-700 to-slate-900 opacity-70 rounded"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-3/4 h-3/4 rounded-lg bg-gradient-to-br from-slate-600 to-slate-800 opacity-80"></div>
                          </div>
                          
                          {/* AI-detected anomaly highlight */}
                          <div className="absolute top-[40%] left-[35%] w-6 h-6">
                            <div className="absolute inset-0 rounded-full border-2 border-red-500 animate-ping opacity-70"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Target size={16} className="text-red-500" />
                            </div>
                          </div>
                          
                          {/* Secondary finding */}
                          <div className="absolute top-[25%] right-[40%] w-4 h-4">
                            <div className="absolute inset-0 rounded-full border-2 border-yellow-400 animate-pulse opacity-70"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-1 h-1 bg-yellow-400 rounded-full"></div>
                            </div>
                          </div>
                          
                          {/* Scan markers */}
                          <div className="absolute top-2 left-2 text-xs text-slate-400">S</div>
                          <div className="absolute top-2 right-2 text-xs text-slate-400">I</div>
                          <div className="absolute bottom-2 left-2 text-xs text-slate-400">A</div>
                          <div className="absolute bottom-2 right-2 text-xs text-slate-400">P</div>
                        </div>
                      </div>
                      
                      {/* Analysis results */}
                      <div className="flex flex-col justify-center space-y-3 p-2">
                        <div className="bg-red-900/50 p-2 rounded border border-red-700/30">
                          <div className="text-xs text-red-300 mb-1">{safeLocalizeSpelling("Primary Finding", currentLocale)}</div>
                          <div className="text-sm font-semibold text-white flex items-center">
                            <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                            {safeLocalizeSpelling("Mass detected", currentLocale)}
                          </div>
                          <div className="text-xs text-red-400 mt-1">{safeLocalizeSpelling("Confidence: 92%", currentLocale)}</div>
                        </div>
                        
                        <div className="bg-yellow-900/50 p-2 rounded border border-yellow-700/30">
                          <div className="text-xs text-yellow-300 mb-1">{safeLocalizeSpelling("Secondary Finding", currentLocale)}</div>
                          <div className="text-sm font-semibold text-white">
                            {safeLocalizeSpelling("Calcification", currentLocale)}
                          </div>
                          <div className="text-xs text-yellow-400 mt-1">{safeLocalizeSpelling("Confidence: 87%", currentLocale)}</div>
                        </div>
                        
                        <div className="bg-cyan-900/50 p-2 rounded border border-cyan-700/30">
                          <div className="text-xs text-cyan-300 mb-1">{safeLocalizeSpelling("Priority", currentLocale)}</div>
                          <div className="text-sm font-semibold text-cyan-200">
                            {safeLocalizeSpelling("Immediate review", currentLocale)}
                          </div>
                          <div className="text-xs text-cyan-400 mt-1">{safeLocalizeSpelling("Similar cases: 847", currentLocale)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Analysis Stats */}
                <div className="w-full md:w-52 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: safeLocalizeSpelling("Detection Accuracy", currentLocale), value: localizedData.imagingStats.detectionAccuracy, color: "text-cyan-700", bgColor:"bg-cyan-100" }, 
                    { label: safeLocalizeSpelling("Analysis Time", currentLocale), value: localizedData.imagingStats.analysisTime, color: "text-green-700", bgColor:"bg-green-100" }, 
                    { label: safeLocalizeSpelling("False Positive Rate", currentLocale), value: localizedData.imagingStats.falsePositiveRate, color: "text-amber-700", bgColor:"bg-amber-100" }, 
                    { label: safeLocalizeSpelling("Cases Processed", currentLocale), value: localizedData.imagingStats.casesProcessed, color: "text-blue-700", bgColor:"bg-blue-100" } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <Activity size={12} className="text-cyan-500"/>
                <span>
                  {safeLocalizeSpelling("AI analysis complete. Radiologist notification sent.", currentLocale)}
                </span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("The Challenge", currentLocale)}</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {localizedData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Our Solution", currentLocale)}</h2>
              <ul className="space-y-3 text-slate-700">
                {localizedData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${localizedData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Key Benefits", currentLocale)}</h2>
              <div className="bg-cyan-50 border border-cyan-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {localizedData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className={`flex items-start text-cyan-800`}>
                      <CheckCircle className={`w-5 h-5 text-cyan-600 mr-3 mt-0.5 flex-shrink-0`} />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Technologies Used", currentLocale)}</h2>
              <div className="flex flex-wrap gap-3">
                {localizedData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${localizedData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-50`}>
              {safeLocalizeSpelling("Enhance Medical Diagnostics", currentLocale)}
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 